import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";

import {
  GameObjectType,
  PointEntity,
  ReplayEntity,
  ReplayEntityV2,
} from "@repo/shared";

export type EventCallback<E> = (event: E) => void;

const initialHandlers = {
  keydown: new Set<EventCallback<KeyboardEvent>>(),
  keyup: new Set<EventCallback<KeyboardEvent>>(),
  pointerdown: new Set<EventCallback<React.PointerEvent<HTMLDivElement>>>(),
  pointermove: new Set<EventCallback<React.PointerEvent<HTMLDivElement>>>(),
  pointerup: new Set<EventCallback<React.PointerEvent<HTMLDivElement>>>(),
  pointercancel: new Set<EventCallback<React.PointerEvent<HTMLDivElement>>>(),
  pointermissed: new Set<EventCallback<MouseEvent>>(),
  touchstart: new Set<EventCallback<React.TouchEvent<HTMLDivElement>>>(),
  touchmove: new Set<EventCallback<React.TouchEvent<HTMLDivElement>>>(),
  touchend: new Set<EventCallback<React.TouchEvent<HTMLDivElement>>>(),
  wheel: new Set<EventCallback<React.WheelEvent<HTMLDivElement>>>(),
  focus: new Set<EventCallback<FocusEvent>>(),
  blur: new Set<EventCallback<FocusEvent>>(),
  ["sim-replay"]: new Set<
    EventCallback<{
      replay: ReplayEntity;
      lastPosition: PointEntity;
      lastVelocity: PointEntity;
    }>
  >(),
  ["sim-step"]: new Set<EventCallback<void>>(),
  ["sim-shard-collected"]: new Set<
    EventCallback<{shard: {position: PointEntity}; steps: number}>
  >(),
  ["new-personal-best"]: new Set<
    EventCallback<{
      replay: ReplayEntityV2;
    }>
  >(),
  ["sim-impulse"]: new Set<
    EventCallback<{
      type: GameObjectType;
      impulse: PointEntity;
      position: PointEntity;
    }>
  >(),
} as const;

// Helper to extract the event type from a Set<EventCallback<E>>
type ExtractPayload<T> = T extends Set<EventCallback<infer E>> ? E : never;

export type GlobalEvents = {
  [K in keyof typeof initialHandlers]: ExtractPayload<
    (typeof initialHandlers)[K]
  >;
};

interface GlobalEventBusContextValue {
  subscribe<K extends keyof GlobalEvents>(
    eventType: K,
    callback: EventCallback<GlobalEvents[K]>,
  ): void;

  unsubscribe<K extends keyof GlobalEvents>(
    eventType: K,
    callback: EventCallback<GlobalEvents[K]>,
  ): void;

  dispatch<K extends keyof GlobalEvents>(
    eventType: K,
    payload: GlobalEvents[K],
  ): void;
}

const GlobalEventBusContext = createContext<GlobalEventBusContextValue | null>(
  null,
);

export const GlobalEventBusProvider: React.FC<{
  children: React.ReactNode;
}> = ({children}) => {
  const handlersRef = useRef(initialHandlers);

  const subscribe = useCallback(
    <K extends keyof GlobalEvents>(
      eventType: K,
      callback: EventCallback<GlobalEvents[K]>,
    ) => {
      handlersRef.current[eventType].add(callback as any);
    },
    [],
  );

  const unsubscribe = useCallback(
    <K extends keyof GlobalEvents>(
      eventType: K,
      callback: EventCallback<GlobalEvents[K]>,
    ) => {
      handlersRef.current[eventType].delete(callback as any);
    },
    [],
  );

  const dispatch = useCallback(
    <K extends keyof GlobalEvents>(eventType: K, payload: GlobalEvents[K]) => {
      handlersRef.current[eventType].forEach((cb) => cb(payload as any));
    },
    [],
  );

  return (
    <GlobalEventBusContext.Provider value={{subscribe, unsubscribe, dispatch}}>
      {children}
    </GlobalEventBusContext.Provider>
  );
};

export function useGlobalEvent<K extends keyof GlobalEvents>(
  eventType: K,
  callback: EventCallback<GlobalEvents[K]>,
) {
  const context = useContext(GlobalEventBusContext);
  if (!context) {
    throw new Error(
      "useGlobalEvent must be used within a GlobalEventBusProvider",
    );
  }
  useEffect(() => {
    context.subscribe(eventType, callback);
    return () => {
      context.unsubscribe(eventType, callback);
    };
  }, [eventType, callback, context]);
}

export function useGlobalEventDispatch() {
  const context = useContext(GlobalEventBusContext);
  if (!context) {
    throw new Error(
      "useGlobalEventDispatch must be used within a GlobalEventBusProvider",
    );
  }
  return context.dispatch;
}

export type GlobalEventDispatch = GlobalEventBusContextValue["dispatch"];
